import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import Button from "components/Button";
import clsx from "clsx";
import { buildingStore } from "store/Building/BuildingStore";
import { BuildingIN, BuildingOUT } from "api-client";
import useToast from "hooks/useToast";
import { AxiosResponse } from "axios";
import { useNavigate } from "react-router";
import { buildingSettingsStore } from "../BuildingSettingsStore";
import { sidebarMenuStore } from "features/SidebarMenu/SidebarMenuStore";
import { ROUTES_ENUM } from "routes/consts";
import DeleteBuilding from "features/Building/BuildingPassport/DeleteBuilding";
import ArchiveBuilding from "features/Building/BuildingPassport/ArchiveBuilding";
import { IconExclamationMark } from "@tabler/icons-react";
import { conformityStore } from "../../../../store/ConformityStore.ts";
import { USER_ROLES } from "utils";
import { useFeatureAccess } from "hooks/useFeatureAccess";
import { LicenseFeatureEnum } from "api-client";
import { usePostHog } from "@posthog/react";

export default observer(function SettingsFooter({
  type,
  onMaxLimitWarning,
  close,
}: {
  type: "add" | "edit";
  onMaxLimitWarning?: React.Dispatch<React.SetStateAction<boolean>>;
  close: () => void;
}) {
  const { t } = useTranslation();
  const showDeletePopup = React.useCallback(() => setOpenDeletePopup(true), []);
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const showArchivePopup = React.useCallback(
    () => setOpenArchivePopup(true),
    []
  );
  const [openArchivePopup, setOpenArchivePopup] = useState(false);
  const organisation = sidebarMenuStore.currentOrganisation;
  const isUserAdmin = organisation?.role === USER_ROLES[0].id;
  const isArchived = buildingStore.isCurrentBuildingArchived;
  const { hasFeature } = useFeatureAccess();
  const canArchive = hasFeature(LicenseFeatureEnum.ArchiveBuilding);
  const { settings } = buildingSettingsStore;
  const toast = useToast();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [submitLoading, setSubmitLoading] = useState(false);

  async function addBuilding() {
    if (!organisation?.id || !settings.name) return;

    const data = buildingSettingsStore.buildingUpdateProps({
      organisation_id: organisation.id,
      name: settings.name,
      certification_id: conformityStore.certificationOptions.find(
        (item) => item.name === "Keine"
      )?.id,
      conformity_id: conformityStore.conformityOptions.find(
        (item) => item.name === "A2"
      )?.id,
    }) as BuildingIN;

    await buildingSettingsStore
      .createBuilding(data)
      .then(async (response) => {
        if (response) await onSuccessAdd(response);
      })
      .catch((err) => onErrorAdd(err));
  }

  async function onSuccessAdd(response: AxiosResponse<BuildingOUT>) {
    navigate(`/${ROUTES_ENUM.BUILDINGS}/${response.data.id}`);
    close();
    toast(t("addBuilding.successAdd"), "success");
    buildingSettingsStore.prefillDGNBFields();
    posthog.capture("building_created", {
      building_id: response.data.id,
      building_name: response.data.name,
      organisation_id: organisation?.id,
    });
  }

  function onErrorAdd(error: {
    response: { status: number };
    message: string;
  }) {
    if (error?.response && error.response.status === 412) {
      close();
      toast(t("dashboard.max_building_heading"), "warning");
      onMaxLimitWarning?.(true);
    } else {
      toast(error.message, "warning");
    }
  }

  async function editBuilding() {
    if (!buildingStore.currentBuilding?.id) return;
    try {
      if (buildingSettingsStore.settings.selectedImages?.length) {
        await buildingSettingsStore.updateBuildingImages();
      }
      await buildingSettingsStore.updateBuilding();
      toast(t("addBuilding.successEdit"), "success");
      close();
    } catch (err) {
      console.log(err);
      toast(err as string, "warning");
    }
  }

  async function submitBuilding() {
    setSubmitLoading(true);
    if (buildingSettingsStore.imagesToRemove.length) {
      await buildingSettingsStore.deleteImages();
    }
    if (settings.selectedImages?.length) {
      await buildingSettingsStore.uploadBuildingImages();
    }
    if (type === "add") {
      await addBuilding();
    } else {
      await editBuilding();
    }
    setSubmitLoading(false);
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    buildingSettingsStore.checkHasValidationsError();
    if (buildingSettingsStore.showMandatoryAlert) return;
    await submitBuilding();
  };

  return (
    <div
      className={clsx(
        "px-6 py-4 bg-gray-50 flex fixed left-0 right-0 bottom-0 justify-between border-t border-gray-100"
      )}
    >
      {type === "add" && (
        <div className="text-sm font-normal text-gray-600 max-w-[60%]">
          {t("buildingSettings.createBuildingDescription")}
        </div>
      )}
      {type === "edit" && (
        <div className="flex gap-3">
          <Button type="danger" width="fit-content" onClick={showDeletePopup}>
            {t("addBuilding.deleteBuilding")}
          </Button>
          {canArchive && (
            <Button
              type="danger"
              width="fit-content"
              onClick={showArchivePopup}
              disable={!isUserAdmin || isArchived}
              tooltip={
                isArchived
                  ? t("buildingSettings.archiveAlready")
                  : !isUserAdmin
                  ? t("buildingSettings.archiveAdminOnly")
                  : ""
              }
            >
              {t("buildingSettings.archiveBuilding")}
            </Button>
          )}
        </div>
      )}
      {buildingSettingsStore.showMandatoryAlert && (
        <div className="text-xs leading-4 font-medium text-red-700 flex items-center">
          <IconExclamationMark
            className="min-w-[20px] w-[20px] h-[20px] mr-2 text-red-600 bg-red-100 rounded-full p-1"
            stroke={3}
          />
          {t("buildingSettings.errorOnMandatoryFields")}
        </div>
      )}
      <Button
        type="primary"
        onClick={submit}
        width="fit-content"
        testID="addbuildingform_button"
        loading={submitLoading}
        disable={submitLoading || buildingSettingsStore.showMandatoryAlert}
      >
        {type === "add"
          ? t("buildingSettings.createBuilding")
          : t("commons.save")}
      </Button>
      {openDeletePopup && (
        <DeleteBuilding open={openDeletePopup} close={setOpenDeletePopup} />
      )}
      {openArchivePopup && (
        <ArchiveBuilding open={openArchivePopup} close={setOpenArchivePopup} />
      )}
    </div>
  );
});
